/**
 * ShareView — resolves a share link and redirects to the project view
 * with the full captured view state (layout, viewport, filters, etc.).
 *
 * URL params (branch, nodes, panel, tab, filters) go into the query string.
 * Component-level state (layout split, DAG zoom, AG Grid columns) is stashed
 * in sessionStorage so the Project page can restore it after mount.
 */

import { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Box, Heading, Text, Spinner } from "@radix-ui/themes";

import { fetchSharedView } from "../api/share";

export const SHARED_VIEW_STATE_KEY = "thread-shared-view-state";

export default function ShareView() {
  const { shareId } = useParams<{ shareId: string }>();
  const navigate = useNavigate();

  const { data, isLoading, error } = useQuery({
    queryKey: ["share", shareId],
    queryFn: () => fetchSharedView(shareId!),
    enabled: !!shareId,
  });

  useEffect(() => {
    if (!data?.config) return;
    const { project_id, viewState, ...params } = data.config as Record<string, unknown>;
    if (!project_id) return;

    if (viewState && typeof viewState === "object") {
      sessionStorage.setItem(SHARED_VIEW_STATE_KEY, JSON.stringify(viewState));
    }

    const search = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value != null && value !== "" && typeof value === "string") {
        search.set(key, value);
      }
    }
    search.set("_shared", "1");
    const qs = search.toString();
    navigate(`/project/${project_id}${qs ? `?${qs}` : ""}`, { replace: true });
  }, [data, navigate]);

  if (isLoading) {
    return (
      <Box p="6" style={{ textAlign: "center" }}>
        <Spinner size="3" />
        <Text mt="3">Loading shared view...</Text>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p="6" style={{ maxWidth: 600, margin: "0 auto" }}>
        <Heading size="4" color="red" mb="2">Share Link Expired or Invalid</Heading>
        <Text color="gray">
          This share link could not be resolved. It may have expired or been revoked.
        </Text>
      </Box>
    );
  }

  if (data?.config && !(data.config as Record<string, string>).project_id) {
    return (
      <Box p="6" style={{ maxWidth: 900, margin: "0 auto" }}>
        <Heading size="5" mb="4">Shared Exploration</Heading>
        <Text color="gray">
          This shared view does not reference a specific project. The raw configuration:
        </Text>
        <pre style={{ marginTop: 12, fontSize: 12, whiteSpace: "pre-wrap" }}>
          {JSON.stringify(data.config, null, 2)}
        </pre>
      </Box>
    );
  }

  return null;
}
